import * as THREE from 'three';
import { EFFECT_LAYER } from '../enhanced-effects';
import { SUN_DIR } from './sky-dome';

// ---------------------------------------------------------------------------
// 4. Volumetric Light Shafts — billboard quads at doorways/windows
// ---------------------------------------------------------------------------

interface LightShaft {
  mesh: THREE.Mesh;
  baseOpacity: number;
}

const SHAFT_POSITIONS: Array<{ pos: THREE.Vector3; scale: THREE.Vector3; rot?: number; warmth?: number }> = [
  // Original positions
  { pos: new THREE.Vector3(0, 3, -38), scale: new THREE.Vector3(3, 6, 1) },
  { pos: new THREE.Vector3(0, 3, 38), scale: new THREE.Vector3(3, 6, 1) },
  { pos: new THREE.Vector3(-50, 3, -40), scale: new THREE.Vector3(2.5, 5, 1), rot: 0.3 },
  { pos: new THREE.Vector3(50, 3, -40), scale: new THREE.Vector3(2.5, 5, 1), rot: -0.3 },
  { pos: new THREE.Vector3(-30, 2.5, 0), scale: new THREE.Vector3(2, 5, 1), rot: 0.2 },
  { pos: new THREE.Vector3(30, 2.5, 0), scale: new THREE.Vector3(2, 5, 1), rot: -0.2 },
  { pos: new THREE.Vector3(-15, 3.5, -60), scale: new THREE.Vector3(3, 6, 1), rot: 0.15 },
  { pos: new THREE.Vector3(15, 3.5, 60), scale: new THREE.Vector3(3, 6, 1), rot: -0.15 },
  // New positions at key map locations
  { pos: new THREE.Vector3(-55, 3, -30), scale: new THREE.Vector3(3.5, 7, 1), rot: 0.25, warmth: 0.15 },   // A site entrance
  { pos: new THREE.Vector3(45, 2.5, 25), scale: new THREE.Vector3(2.8, 5.5, 1), rot: -0.1, warmth: -0.05 }, // B tunnels exit
  { pos: new THREE.Vector3(5, 3.5, 10), scale: new THREE.Vector3(2.5, 6, 1), rot: 0.05, warmth: 0.1 },     // Mid doors
  { pos: new THREE.Vector3(-52, 3, 25), scale: new THREE.Vector3(3, 6.5, 1), rot: 0.35, warmth: 0.2 },     // Long A corridor
];

export function createLightShafts(scene: THREE.Scene): {
  shafts: LightShaft[];
  update: (dt: number, camPos: THREE.Vector3) => void;
  dispose: () => void;
} {
  const shaftGeo = new THREE.PlaneGeometry(1, 1);
  const shafts: LightShaft[] = [];

  for (const def of SHAFT_POSITIONS) {
    const warmthOffset = def.warmth ?? 0.0;

    const mat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      uniforms: {
        uOpacity: { value: 0.0 },
        uTime: { value: 0.0 },
        uWarmth: { value: warmthOffset },
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float uOpacity;
        uniform float uTime;
        uniform float uWarmth;
        varying vec2 vUv;

        float hash(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
        }

        float noise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          f = f * f * (3.0 - 2.0 * f);
          float a = hash(i);
          float b = hash(i + vec2(1.0, 0.0));
          float c = hash(i + vec2(0.0, 1.0));
          float d = hash(i + vec2(1.0, 1.0));
          return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
        }

        void main() {
          // Soft gradient: bright at top, fading down
          float gradient = smoothstep(0.0, 0.25, vUv.y) * smoothstep(1.0, 0.65, vUv.y);
          // Soft horizontal edges
          float edgeX = smoothstep(0.0, 0.2, vUv.x) * smoothstep(1.0, 0.8, vUv.x);
          // Subtle flicker
          float flicker = 0.9 + 0.1 * sin(uTime * 1.5 + vUv.y * 3.0);

          // Volumetric dust-in-beam noise
          vec2 noiseUV = vUv * vec2(4.0, 8.0) + vec2(uTime * 0.3, -uTime * 0.15);
          float dustNoise = noise(noiseUV) * 0.6 + noise(noiseUV * 2.5) * 0.3;
          // Subtle modulation of shaft brightness by dust
          float dustFactor = 0.7 + dustNoise * 0.6;

          float alpha = gradient * edgeX * uOpacity * flicker * dustFactor;

          // Color variation per shaft: base warm gold + warmth offset
          vec3 color = vec3(1.0 + uWarmth * 0.2, 0.92 + uWarmth * 0.3, 0.7 - uWarmth * 0.1);
          color = clamp(color, 0.0, 1.5);

          gl_FragColor = vec4(color, alpha);
        }
      `,
    });

    const mesh = new THREE.Mesh(shaftGeo, mat);
    mesh.position.copy(def.pos);
    mesh.scale.copy(def.scale);
    if (def.rot) mesh.rotation.y = def.rot;
    mesh.userData.isEffect = true;
    mesh.layers.set(EFFECT_LAYER);
    mesh.renderOrder = 90;
    scene.add(mesh);

    // Higher base opacity: 0.18 - 0.26
    shafts.push({ mesh, baseOpacity: 0.18 + Math.random() * 0.08 });
  }

  const _sunDir = SUN_DIR.clone();
  const _camToShaft = new THREE.Vector3();
  const _camDir = new THREE.Vector3();
  // F-RN-016: scratch Vector3 reused across all shafts/frames so the lookAt
  // call goes through the (Vector3) overload (no per-call internal allocation
  // path) rather than the (x,y,z) overload.
  const _lookAtTarget = new THREE.Vector3();

  return {
    shafts,
    update(dt: number, camPos: THREE.Vector3) {
      for (const shaft of shafts) {
        const mat = shaft.mesh.material as THREE.ShaderMaterial;
        mat.uniforms.uTime.value += dt;

        // Billboard: face camera on Y axis (reuse scratch — F-RN-016)
        _lookAtTarget.set(camPos.x, shaft.mesh.position.y, camPos.z);
        shaft.mesh.lookAt(_lookAtTarget);

        // Fade based on view angle relative to sun
        _camToShaft.subVectors(shaft.mesh.position, camPos).normalize();
        const sunAlignment = Math.max(0, _camToShaft.dot(_sunDir));
        const angleFade = Math.pow(sunAlignment, 0.5) * 0.7 + 0.3;

        // Distance fade
        const dist = camPos.distanceTo(shaft.mesh.position);
        const distFade = 1.0 - Math.min(1, Math.max(0, (dist - 5) / 60));

        mat.uniforms.uOpacity.value = shaft.baseOpacity * angleFade * distFade;
      }
    },
    dispose() {
      for (const shaft of shafts) {
        scene.remove(shaft.mesh);
        (shaft.mesh.material as THREE.ShaderMaterial).dispose();
      }
      shaftGeo.dispose();
    },
  };
}
